import { useState, useEffect, useCallback } from 'react'
import {
  MdArrowBack,
  MdRefresh,
  MdCalendarToday,
  MdInfo,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../../components/ui/Button'
import EmptyState from '../../components/ui/EmptyState'
import FilterBar from '../../components/ui/FilterBar'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import SearchBar from '../../components/ui/SearchBar'
import { useSimulation } from '../../hooks/useSimulation'

function SimulationHistoryPage() {
  const { simulationNumber } = useParams()
  const navigate = useNavigate()
  const [simulation, setSimulation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterAction, setFilterAction] = useState('all')

  const { getSimulationAsync, isGetSimulationLoading } = useSimulation()

  const loadSimulation = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getSimulationAsync(simulationNumber)
      setSimulation(data)
    } catch (error) {
      console.error('Error loading simulation:', error)
      setError('Failed to load simulation history')
    } finally {
      setIsLoading(false)
    }
  }, [simulationNumber]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (simulationNumber) {
      loadSimulation()
    }
  }, [simulationNumber, loadSimulation])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const formatAction = (action) => {
    if (!action) return 'Unknown'
    return action.replace(/_/g, ' ').toLowerCase()
  }

  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case 'status_updated':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
      case 'created':
        return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
      case 'deleted':
        return 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
      case 'modified':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const getFilteredHistory = () => {
    if (!simulation.history) return []

    let filtered = simulation.history

    // Filter by action type
    if (filterAction !== 'all') {
      filtered = filtered.filter(
        (entry) => entry.action?.toLowerCase() === filterAction.toLowerCase(),
      )
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (entry) =>
          entry.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.details?.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          entry.details?.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    return filtered
  }

  const getPaginatedHistory = () => {
    const filtered = getFilteredHistory()
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filtered.slice(startIndex, endIndex)
  }

  const getTotalPages = () => {
    const filtered = getFilteredHistory()
    return Math.ceil(filtered.length / itemsPerPage)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const getUniqueActions = () => {
    if (!simulation.history) return []
    const actions = simulation.history.map((entry) => entry.action).filter(Boolean)
    return [...new Set(actions)]
  }

  const resetFilters = () => {
    setSearchTerm('')
    setFilterAction('all')
    setCurrentPage(1)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !simulation) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="text-left">
            <Button
              variant="ghost"
              onClick={() => navigate(`/simulations/${simulationNumber}`)}
              className="flex items-center space-x-2 mb-4"
            >
              <MdArrowBack />
              <span>Back to Simulation</span>
            </Button>
          </div>

          <EmptyState
            icon={<MdInfo />}
            title="History Not Found"
            description={
              error ||
              "The simulation history you're looking for doesn't exist or has been removed."
            }
            action={
              <Button
                variant="primary"
                onClick={() => navigate(`/simulations/${simulationNumber}`)}
              >
                Back to Simulation
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  const filteredHistory = getFilteredHistory()
  const paginatedHistory = getPaginatedHistory()
  const totalPages = getTotalPages()
  const uniqueActions = getUniqueActions()

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left">
          <Button
            variant="ghost"
            onClick={() => navigate(`/simulations/${simulationNumber}`)}
            className="flex items-center space-x-2 mb-4"
          >
            <MdArrowBack />
            <span>Back to Simulation</span>
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2 text-left">
              Simulation History
            </h1>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 text-left">
              {simulation.name || `Simulation #${simulation.simulationNumber}`}
            </p>
          </div>

          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button variant="ghost" onClick={loadSimulation} disabled={isGetSimulationLoading}>
              <MdRefresh className={isGetSimulationLoading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center">
            <MdInfo className="mr-2 text-primary-600 dark:text-primary-400" />
            Filters & Search
          </h2>
          {(searchTerm || filterAction !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={resetFilters}
              className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
            >
              Clear All Filters
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Search History
            </label>
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              placeholder="Search history..."
            />
          </div>

          {/* Action Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Filter by Action
            </label>
            <FilterBar
              filterValue={filterAction}
              onFilterChange={(e) => {
                setFilterAction(e.target.value)
                setCurrentPage(1)
              }}
              filterOptions={[
                { value: 'all', label: 'All Actions' },
                ...uniqueActions.map((action) => ({
                  value: action,
                  label: formatAction(action),
                })),
              ]}
            />
          </div>

          {/* Results Count */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Results
            </label>
            <div className="flex items-center justify-center h-12 px-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <MdCalendarToday className="mr-2 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                {filteredHistory.length} of {simulation.history?.length || 0} entries
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white flex items-center">
            <MdInfo className="mr-2 text-primary-600 dark:text-primary-400" />
            History Entries
          </h2>
          <div className="text-sm text-neutral-600 dark:text-neutral-400">
            Page {currentPage} of {totalPages}
          </div>
        </div>

        {/* Debug Information */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center mb-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Debug Information
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-blue-700 dark:text-blue-300">Total entries:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {simulation.history?.length || 0}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Filtered:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {filteredHistory.length}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Current page:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {currentPage}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Items per page:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {itemsPerPage}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Total pages:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {totalPages}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Showing:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {(currentPage - 1) * itemsPerPage + 1} -{' '}
                {Math.min(currentPage * itemsPerPage, filteredHistory.length)}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">On this page:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {paginatedHistory.length}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Dark mode:</span>
              <span className="ml-1 font-medium text-blue-900 dark:text-blue-100">
                {document.documentElement.classList.contains('dark') ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {paginatedHistory.length === 0 ? (
          <EmptyState
            icon={<MdInfo />}
            title="No History Found"
            description={
              filteredHistory.length === 0
                ? 'No history entries match your current filters.'
                : 'No history entries available.'
            }
            action={
              filteredHistory.length === 0 && (searchTerm || filterAction !== 'all') ? (
                <Button variant="primary" onClick={resetFilters}>
                  Clear Filters
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <div className="space-y-4">
              {paginatedHistory.map((entry, index) => (
                <div
                  key={entry._id || index}
                  className="flex items-start space-x-4 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
                >
                  <div className="w-3 h-3 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span
                          className={`px-3 py-1 text-sm font-medium rounded-full ${getActionColor(entry.action)}`}
                        >
                          {formatAction(entry.action)}
                        </span>
                        <span className="text-sm text-neutral-500 dark:text-neutral-400">
                          {formatDate(entry.timestamp)}
                        </span>
                      </div>
                    </div>

                    {entry.details && (
                      <div className="space-y-1">
                        {entry.details.status && (
                          <p className="text-sm text-neutral-700 dark:text-neutral-300">
                            <span className="font-medium">Status:</span> {entry.details.status}
                          </p>
                        )}
                        {entry.details.createdBy && (
                          <p className="text-sm text-neutral-700 dark:text-neutral-300">
                            <span className="font-medium">Created by:</span>{' '}
                            {entry.details.createdBy}
                          </p>
                        )}
                        {entry.details.initialStatus && (
                          <p className="text-sm text-neutral-700 dark:text-neutral-300">
                            <span className="font-medium">Initial status:</span>{' '}
                            {entry.details.initialStatus}
                          </p>
                        )}
                        {entry.details.timestamp && (
                          <p className="text-sm text-neutral-700 dark:text-neutral-300">
                            <span className="font-medium">Details timestamp:</span>{' '}
                            {formatDate(entry.details.timestamp)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-neutral-600 dark:text-neutral-400 text-center sm:text-left">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, filteredHistory.length)} of{' '}
                    {filteredHistory.length} entries
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="flex-shrink-0"
                    >
                      <MdChevronLeft />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>

                    <div className="flex items-center gap-1 overflow-x-auto max-w-48 px-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'primary' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className="w-8 h-8 p-0 flex-shrink-0"
                        >
                          {page}
                        </Button>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="flex-shrink-0"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <MdChevronRight />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SimulationHistoryPage
